// src/utils/stepContent.ts - Static content for each BCP step

export interface StepNextStep {
  text: string;
  priority?: 'high' | 'medium' | 'low';
  checkComplete?: (data: any) => boolean; // Function to check if this step is complete
}

export interface StepContentData {
  title: string;
  description: string;
  nextSteps: StepNextStep[];
}

export const STEP_CONTENT: Record<number, StepContentData> = {
  1: {
    title: 'Business Information',
    description: 'Complete your business profile to get personalized insights',
    nextSteps: [
      {
        text: 'Complete business name and industry details',
        priority: 'high',
        checkComplete: (data) => !!(data.formData?.business_name && data.formData?.industry)
      },
      {
        text: 'Specify your business type and size',
        priority: 'high',
        checkComplete: (data) => !!(data.formData?.business_type && data.formData?.employee_count > 0)
      },
      {
        text: 'Add primary business location information',
        priority: 'high',
        checkComplete: (data) => !!(
          data.formData?.primary_location?.name &&
          data.formData?.primary_location?.address &&
          data.formData?.primary_location?.state &&
          data.formData?.primary_location?.zip
        )
      },
      {
        text: 'Provide contact information for the primary location',
        priority: 'medium',
        checkComplete: (data) => !!(
          data.formData?.primary_location?.phone ||
          data.formData?.primary_location?.email
        )
      },
      {
        text: 'Add additional locations if your business operates across multiple sites',
        priority: 'low',
        checkComplete: (data) => data.formData?.additional_locations?.length > 0
      }
    ]
  },
  2: {
    title: 'Risk Assessment',
    description: 'Identify potential risks and threats to your business',
    nextSteps: [
      {
        text: 'Review and select primary business concerns',
        priority: 'high',
        checkComplete: (data) => data.risks?.primary_concerns?.length > 0
      },
      {
        text: 'Identify location-specific risks (natural disasters, etc.)',
        priority: 'high',
        checkComplete: (data) => data.risks?.location_risks?.length > 0
      },
      {
        text: 'Consider cyber security and technology risks',
        priority: 'high',
        checkComplete: (data) => {
          const concerns = data.risks?.primary_concerns || [];
          return concerns.some((c: string) =>
            c.toLowerCase().includes('cyber') ||
            c.toLowerCase().includes('technology') ||
            c.toLowerCase().includes('data breach') ||
            c.toLowerCase().includes('ransomware')
          );
        }
      },
      {
        text: 'Assess supply chain and vendor risks',
        priority: 'medium',
        checkComplete: (data) => {
          const concerns = data.risks?.primary_concerns || [];
          return concerns.some((c: string) =>
            c.toLowerCase().includes('supply') ||
            c.toLowerCase().includes('vendor') ||
            c.toLowerCase().includes('supplier')
          );
        }
      },
      {
        text: 'Document key personnel and succession risks',
        priority: 'medium',
        checkComplete: (data) => {
          const concerns = data.risks?.primary_concerns || [];
          return concerns.some((c: string) =>
            c.toLowerCase().includes('personnel') ||
            c.toLowerCase().includes('key person') ||
            c.toLowerCase().includes('succession')
          );
        }
      }
    ]
  },
  3: {
    title: 'Business Impact Analysis',
    description: 'Assess the impact of disruptions on your operations',
    nextSteps: [
      {
        text: 'Define Maximum Tolerable Downtime (MTD)',
        priority: 'high',
        checkComplete: (data) => {
          // MTD is stored at formData.max_downtime (root level)
          return !!(data.formData?.max_downtime);
        }
      },
      {
        text: 'Set Recovery Priority based on business needs',
        priority: 'high',
        checkComplete: (data) => {
          // Recovery Priority is stored at formData.recovery_priority (root level)
          return !!(data.formData?.recovery_priority);
        }
      },
      {
        text: 'Complete Risk Impact Assessment Matrix for all identified risks',
        priority: 'high',
        checkComplete: (data) => {
          const assessments = data.formData?.risk_impact_assessments || {};
          const primaryConcerns = data.risks?.primary_concerns || [];
          const locationRisks = data.risks?.location_risks || [];
          const totalRisks = primaryConcerns.length + locationRisks.length;
          if (totalRisks === 0) return false;

          const completedPrimary = primaryConcerns.filter((risk: string) =>
            assessments.primary_concerns?.[risk]?.likelihood &&
            assessments.primary_concerns?.[risk]?.impact
          ).length;
          const completedLocation = locationRisks.filter((risk: string) =>
            assessments.location_risks?.[risk]?.likelihood &&
            assessments.location_risks?.[risk]?.impact
          ).length;

          return (completedPrimary + completedLocation) >= totalRisks * 0.8; // 80% complete
        }
      },
      {
        text: 'Assess financial impact of business interruptions',
        priority: 'medium',
        checkComplete: (data) => {
          const assessments = data.formData?.risk_impact_assessments || {};
          const allRisks = [...(data.risks?.primary_concerns || []), ...(data.risks?.location_risks || [])];
          return allRisks.some((risk: string) =>
            assessments.primary_concerns?.[risk]?.financial_impact ||
            assessments.location_risks?.[risk]?.financial_impact
          );
        }
      },
      {
        text: 'Evaluate operational and reputational impacts',
        priority: 'medium',
        checkComplete: (data) => {
          const assessments = data.formData?.risk_impact_assessments || {};
          const allRisks = [...(data.risks?.primary_concerns || []), ...(data.risks?.location_risks || [])];
          return allRisks.some((risk: string) =>
            assessments.primary_concerns?.[risk]?.operational_impact ||
            assessments.location_risks?.[risk]?.operational_impact
          );
        }
      }
    ]
  },
  4: {
    title: 'Critical Operations',
    description: 'Define your critical business functions and processes',
    nextSteps: [
      {
        text: 'Document all critical business functions',
        priority: 'high',
        checkComplete: (data) => data.formData?.critical_functions?.length > 0
      },
      {
        text: 'Define RTO (Recovery Time Objective) and RPO (Recovery Point Objective) for each function',
        priority: 'high',
        checkComplete: (data) => {
          const functions = data.formData?.critical_functions || [];
          return functions.some((fn: any) => fn.rto && fn.rpo);
        }
      },
      {
        text: 'Identify and document all IT systems and dependencies',
        priority: 'high',
        checkComplete: (data) => data.formData?.it_systems?.length > 0
      },
      {
        text: 'List key suppliers and alternative suppliers',
        priority: 'high',
        checkComplete: (data) => data.formData?.key_suppliers?.length > 0
      },
      {
        text: 'Specify recovery resources and responsible parties',
        priority: 'medium',
        checkComplete: (data) => {
          const functions = data.formData?.critical_functions || [];
          return functions.some((fn: any) =>
            fn.responsible_party || fn.owner || fn.resources?.length > 0
          );
        }
      }
    ]
  },
  5: {
    title: 'Mitigations',
    description: 'Define strategies to mitigate identified risks',
    nextSteps: [
      {
        text: 'Create mitigation strategies for each identified risk',
        priority: 'high',
        checkComplete: (data) => {
          const mitigations = data.formData?.mitigations || [];
          const allRisks = [...(data.risks?.primary_concerns || []), ...(data.risks?.location_risks || [])];
          if (allRisks.length === 0) return false;
          // At least 50% of risks should have mitigations
          return mitigations.length >= allRisks.length * 0.5;
        }
      },
      {
        text: 'Define specific actions and procedures for each mitigation',
        priority: 'high',
        checkComplete: (data) => {
          const mitigations = data.formData?.mitigations || [];
          return mitigations.some((m: any) =>
            m.specific_actions?.length > 0 || m.action_steps
          );
        }
      },
      {
        text: 'Assign responsible parties for mitigation implementation',
        priority: 'high',
        checkComplete: (data) => {
          const mitigations = data.formData?.mitigations || [];
          return mitigations.some((m: any) => m.responsible_party);
        }
      },
      {
        text: 'Set timelines and priorities for mitigation activities',
        priority: 'medium',
        checkComplete: (data) => {
          const mitigations = data.formData?.mitigations || [];
          return mitigations.some((m: any) =>
            m.implementation_timeframe || m.implementation_priority
          );
        }
      },
      {
        text: 'Document preventive and responsive measures',
        priority: 'medium',
        checkComplete: (data) => {
          const mitigations = data.formData?.mitigations || [];
          return mitigations.some((m: any) =>
            m.strategy_type === 'Preventive' || m.strategy_type === 'Corrective'
          );
        }
      }
    ]
  },
  6: {
    title: 'Review',
    description: 'Review and finalize your business continuity plan',
    nextSteps: [
      {
        text: 'Review all sections for completeness and accuracy',
        priority: 'high',
        checkComplete: () => false // Always show during review
      },
      {
        text: 'Verify contact information and role assignments',
        priority: 'high',
        checkComplete: (data) => data.employees?.length > 0
      },
      {
        text: 'Ensure all critical functions have recovery procedures',
        priority: 'high',
        checkComplete: (data) => {
          const functions = data.formData?.critical_functions || [];
          return functions.every((fn: any) => fn.recovery_procedures);
        }
      },
      {
        text: 'Generate your complete BCP document',
        priority: 'medium',
        checkComplete: () => false // Always show
      },
      {
        text: 'Schedule regular reviews and updates of your BCP',
        priority: 'low',
        checkComplete: () => false // Always show
      }
    ]
  }
};

export const getStepContent = (step: number): StepContentData => {
  return STEP_CONTENT[step] || STEP_CONTENT[1];
};

export const getStepTitle = (step: number): string => {
  return STEP_CONTENT[step]?.title || 'AI Analysis';
};

export const getStepDescription = (step: number): string => {
  return STEP_CONTENT[step]?.description || 'AI-powered insights for your BCP';
};

export const getStepNextSteps = (step: number): StepNextStep[] => {
  return STEP_CONTENT[step]?.nextSteps || [];
};

export interface StepNextStepWithStatus extends StepNextStep {
  isComplete?: boolean;
}

/**
 * Get filtered next steps based on form data completion
 * Only returns incomplete steps to guide the user on what's left to do
 */
export const getFilteredNextSteps = (
  step: number,
  formData: any,
  risks?: any,
  employees?: any[]
): StepNextStep[] => {
  const allSteps = STEP_CONTENT[step]?.nextSteps || [];

  const contextData = {
    formData,
    risks,
    employees
  };

  // Filter out completed steps - only show what's left to do
  return allSteps.filter(stepItem => {
    if (!stepItem.checkComplete) {
      // If no check function, always show (backwards compatibility)
      return true;
    }

    try {
      // Return true if NOT complete (we want to show incomplete items)
      return !stepItem.checkComplete(contextData);
    } catch (error) {
      // On error, show the step (safer to show than hide)
      console.error('Error checking step completion:', error);
      return true;
    }
  });
};

/**
 * Get all next steps with completion status
 * Useful for showing progress with completed items marked
 */
export const getNextStepsWithStatus = (
  step: number,
  formData: any,
  risks?: any,
  employees?: any[]
): StepNextStepWithStatus[] => {
  const allSteps = STEP_CONTENT[step]?.nextSteps || [];

  const contextData = {
    formData,
    risks,
    employees
  };

  return allSteps.map(stepItem => {
    let isComplete = false;

    if (stepItem.checkComplete) {
      try {
        isComplete = stepItem.checkComplete(contextData);
      } catch (error) {
        console.error('Error checking step completion:', error);
        isComplete = false;
      }
    }

    return {
      ...stepItem,
      isComplete
    };
  });
};



